import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { ArrowLeft, Layers, Star, BookOpen, ArrowRight } from 'lucide-react';
import { loadAllArticles } from '@/services/articleLoader';
import { MainNavigation } from '@/components/MainNavigation';
import { Footer } from '@/components/Footer';
import { Breadcrumb } from '@/components/Breadcrumb';
import { GridBackground } from '@/components/GridBackground';
import { CTA1 } from '@/components/CTABanners';

/**
 * ClusterPage - v3.5 Cluster Definition Page
 * 
 * Dynamic page for each cluster:
 * /clusters/setup-guides
 * /clusters/regional-iptv
 * etc.
 */
export const ClusterPage = () => {
    const { clusterId } = useParams();
    const [articles, setArticles] = useState([]);
    const [pillar, setPillar] = useState(null);
    const [clusterArticles, setClusterArticles] = useState([]);
    const [loading, setLoading] = useState(true);

    // Convert slug to category name
    const categoryName = clusterId
        ?.split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

    useEffect(() => {
        const loadCluster = async () => {
            try {
                const allArticles = await loadAllArticles();
                setArticles(allArticles);

                // Find articles in this cluster
                const filtered = allArticles.filter(a => {
                    const cat = (a.category || '').toLowerCase().replace(/\s+/g, '-');
                    return cat === clusterId;
                });

                // Separate pillar from regular articles
                const pillarArticle = filtered.find(a =>
                    a.layoutType === 'pillar' || a.type === 'pillar'
                );
                const regularArticles = filtered
                    .filter(a => a.layoutType !== 'pillar' && a.type !== 'pillar')
                    .sort((a, b) => (b.score || 0) - (a.score || 0));

                setPillar(pillarArticle);
                setClusterArticles(regularArticles);
            } catch (err) {
                console.error('Error loading cluster:', err);
            } finally {
                setLoading(false);
            }
        };

        loadCluster();
    }, [clusterId]);

    const breadcrumbs = [
        { name: 'Blog', url: '/blog' },
        { name: categoryName, url: null }
    ];

    if (loading) {
        return (
            <>
                <MainNavigation />
                <div className="min-h-screen flex items-center justify-center">
                    <div className="text-center text-muted-foreground">Loading cluster...</div>
                </div>
            </>
        );
    }

    return (
        <>
            <Helmet>
                <title>{categoryName} Cluster | XtreamGate Knowledge Hub</title>
                <meta name="description" content={`Explore the ${categoryName} cluster - comprehensive guides, tutorials, and expert content on ${categoryName.toLowerCase()}.`} />
            </Helmet>

            <MainNavigation />

            <div className="min-h-screen relative">
                <GridBackground />
                <main className="py-20 px-4 relative z-10">
                    <div className="max-w-6xl mx-auto">
                        <Breadcrumb items={breadcrumbs} />

                        {/* Header */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mb-12"
                        >
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-3 bg-primary/20 rounded-xl">
                                    <Layers className="w-6 h-6 text-primary" />
                                </div>
                                <div>
                                    <h1 className="text-3xl sm:text-4xl font-bold">{categoryName}</h1>
                                    <p className="text-muted-foreground">{clusterArticles.length + (pillar ? 1 : 0)} articles</p>
                                </div>
                            </div>
                        </motion.div>

                        {/* Pillar Section */}
                        {pillar && (
                            <motion.section
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.1 }}
                                className="mb-12"
                            >
                                <div className="flex items-center gap-2 mb-4">
                                    <Star className="w-4 h-4 text-primary" />
                                    <h2 className="font-semibold text-lg">Featured Guide</h2>
                                </div>
                                <Link
                                    to={pillar.canonicalPath || `/blog/${pillar.slug}`}
                                    className="block p-6 rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 hover:border-primary/40 transition-all group"
                                >
                                    <h3 className="text-xl font-bold mb-2 group-hover:text-primary transition-colors">
                                        {pillar.title}
                                    </h3>
                                    <p className="text-muted-foreground line-clamp-2 mb-4">
                                        {pillar.excerpt}
                                    </p>
                                    <span className="inline-flex items-center text-primary font-medium">
                                        Read the Complete Guide <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                                    </span>
                                </Link>
                            </motion.section>
                        )}

                        {/* Articles Grid */}
                        <motion.section
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                        >
                            <div className="flex items-center gap-2 mb-6">
                                <BookOpen className="w-4 h-4 text-muted-foreground" />
                                <h2 className="font-semibold text-lg">All Articles</h2>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {clusterArticles.map((article, index) => (
                                    <motion.div
                                        key={article.id}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.05 * index }}
                                    >
                                        <Link
                                            to={article.canonicalPath || `/blog/${article.slug}`}
                                            className="block p-4 rounded-xl bg-white/5 border border-white/10 hover:border-primary/30 hover:bg-white/10 transition-all group"
                                        >
                                            <h3 className="font-semibold mb-1 group-hover:text-primary transition-colors line-clamp-1">
                                                {article.title}
                                            </h3>
                                            <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                                                {article.excerpt}
                                            </p>
                                            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                                <span>{article.readTime}</span>
                                                {article.contentType && (
                                                    <span className="px-2 py-0.5 bg-white/10 rounded capitalize">
                                                        {article.contentType}
                                                    </span>
                                                )}
                                            </div>
                                        </Link>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.section>

                        {/* Back Link */}
                        <div className="mt-8 mb-12">
                            <Link
                                to="/blog"
                                className="inline-flex items-center gap-2 text-muted-foreground hover:text-white transition-colors"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                Back to Blog
                            </Link>
                        </div>

                        {/* CTA Section */}
                        <div className="mt-8">
                            <CTA1
                                headline="Ready to Start Streaming?"
                                subtext={`Explore all ${categoryName.toLowerCase()} content and start your IPTV journey today.`}
                                buttonText="Start Free Trial"
                                buttonUrl="https://xtreamcube.shop/freetrialiptv/"
                                showIcon={false}
                            />
                        </div>
                    </div>
                </main>
            </div>

            <Footer />
        </>
    );
};

export default ClusterPage;
